import { EllipsisHorizontalIcon, PencilSquareIcon, TrashIcon, UserMinusIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

export const UserActionsDropdown = ({ 
    user, 
    isOpen, 
    onClose,
    onToggle,
    onRemoveClick,
    onDisableClick,
    onEnableClick,
    onEditClick,
    compact = false
}) => {
    const buttonRef = useRef(null);
    const [dropdownStyles, setDropdownStyles] = useState({ top: 0, left: 0 });

    const calculatePosition = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyles({
            top: rect.bottom + window.scrollY,
            left: (rect.left + window.scrollX) - 88,
        });
    };

    useEffect(() => {
        if (isOpen) {
            calculatePosition();
            window.addEventListener("scroll", calculatePosition);
            window.addEventListener("resize", calculatePosition);
        }
        return () => {
            window.removeEventListener("scroll", calculatePosition);
            window.removeEventListener("resize", calculatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isOpen &&
                !buttonRef.current?.contains(event.target) &&
                !(event.target.closest && event.target.closest(".user-actions-dropdown"))
            ) {
                onToggle(null);
                onClose?.();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onToggle]);

    return (
        <>
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle(isOpen ? null : user.id);
                }}
                className={`group inline-flex items-center justify-center whitespace-nowrap text-xs font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] ${compact ? "h-6 w-6 rounded-md p-0 text-gray-400 hover:bg-gray-100" : "h-7 gap-1 rounded-md px-2 hover:bg-gray-200"}`}
            >
                <EllipsisHorizontalIcon className={compact ? "h-4 w-4 transition-colors group-hover:text-gray-700" : "h-5 w-5 transition-colors group-hover:text-white"} />
            </button>

            {isOpen && (
                <div
                    className="fixed user-actions-dropdown z-50 w-32 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden"
                    style={(dropdownStyles.top > 0 && dropdownStyles.left > 0) ? dropdownStyles : { display: "none" }}
                >
                    <button className="w-full flex flex-row gap-2 items-center text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEditClick?.(user);
                        onClose?.();
                    }}>
                        <PencilSquareIcon className="h-5 w-5" />
                        Edit
                    </button>
                    {user?.status === "DELETED" ? (
                        <button className="w-full flex flex-row gap-2 items-center text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onEnableClick?.(user);
                            onClose?.();
                        }}>
                            <UserPlusIcon className="h-5 w-5" />
                            Enable
                        </button>
                    ) : (
                        <button className="w-full flex flex-row gap-2 items-center text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDisableClick?.(user);
                            onClose?.();
                        }}>
                            <UserMinusIcon className="h-5 w-5" />
                            Disable
                        </button>
                    )}
                    <button className="w-full flex flex-row gap-2 items-center text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveClick?.(user);
                        onClose?.();
                    }}>
                        <TrashIcon className="h-5 w-5" />
                        Remove
                    </button>
                </div>
            )}
        </>
    );
};